import { isLoadedDefined } from "common/components/component-helpers";
import type {
    EvaluatedScreen,
    ForceValueProducer,
    QuotaBannerMessage,
    RenderEnvironment,
} from "common/components/types";
import { renderValueProducer, SubscribingReactComponent } from "common/components/uis";
import type { TabDescription } from "common/description";
import { withNetworkStatus } from "hooks/use-network-status";
import * as React from "react";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { mergeTheme } from "sharedUI/lib/theme-utils";
import { withTheme } from "styled-components";
import type { OverlayTheme } from "../../../lib/theme/overlay-theme";
import type { RuntimeTheme } from "../../../lib/theme/theme";
import { forcePaddedListOverlay } from "../../../lib/theme/theme-overlays/color-theme-overlay";
import ScrollHistory from "../../smart/scroll-history/scroll-history";
import { scrollIDForScreenID } from "../../smart/scroll-reactive-component/scroll-reactive-component";
import ScrollView from "../scroll-view/scroll-view";

interface ScreenRendererProps {
    onLine: boolean;
    screen: EvaluatedScreen;
    isSubScreen: boolean;
    renderEnvironment: RenderEnvironment;
    inTab: boolean;
    topPadding?: number;
    theme: RuntimeTheme;
    currentTab: TabDescription;
    idSuffix?: string;
}
export const TabContext = React.createContext<TabDescription | undefined>(undefined);

function computeTopPadding(
    renderEnvironment: RenderEnvironment,
    hasSearchBar: boolean,
    hasPivot: boolean,
    theme: any,
    hasQuotaBanner: boolean,
    hasOfflineBanner: boolean
) {
    if (renderEnvironment.displayContext === "map-detail") return 0;

    let result = 0;
    if (hasSearchBar && theme.searchBar.style === "Inline") {
        result += theme.searchBar.heightWithPadding;
    }
    if (hasPivot) {
        result += theme.pivot.height + theme.pivot.bottomMargin;
    }
    if (hasQuotaBanner) {
        result += theme.quotaBar.height;
    }
    if (hasOfflineBanner) {
        result += theme.offlineBanner.height;
        result += theme.offlineBanner.gap;
    }

    return result;
}

class ScreenContentRenderer extends SubscribingReactComponent<ScreenRendererProps, {}> {
    public expandState(_props: ScreenRendererProps, oldState: {} | null): {} {
        if (oldState !== null) return oldState;
        return {};
    }

    public recompute(force: ForceValueProducer): React.ReactNode {
        const { idSuffix, screen, inTab, renderEnvironment, currentTab, topPadding } = this.props;
        const ctx = screen.ctx;

        // If this is loaded and defined then it's a `QuotaBannerMessager | false`.
        // If it's not loaded or defined then we should probably treat it like `false`,
        // i.e. not show a banner.
        const quotaBannerMessage = force(screen.quotaBannerMessage);
        let message: QuotaBannerMessage | false = false;
        const useBanner = true;
        if (isLoadedDefined(quotaBannerMessage) && useBanner) {
            message = quotaBannerMessage as QuotaBannerMessage;
        }

        // Doing it this way instead of with refs because refs get nulled out
        // by react just before the point where we need to use them in componentWillUnmount
        const scrollTarget = scrollIDForScreenID(screen.screenId);

        let theme = this.props.theme;
        const toMerge: OverlayTheme[] = [];

        if (screen.pivotPicker !== undefined && theme.pivot.style === "TopTabs") {
            toMerge.push(forcePaddedListOverlay);
        }

        theme = mergeTheme(theme, toMerge);

        const scrollBar = renderValueProducer(screen.topBar, force, ctx, renderEnvironment, true);

        return (
            <TailwindThemeProvider theme={theme}>
                <TabContext.Provider value={currentTab}>
                    <ScrollView
                        topPadding={
                            topPadding === undefined
                                ? computeTopPadding(
                                      renderEnvironment,
                                      scrollBar !== undefined,
                                      screen.pivotPicker !== undefined,
                                      this.props.theme,
                                      message !== false,
                                      !this.props.onLine
                                  )
                                : topPadding
                        }
                        id={`${scrollTarget}${idSuffix ?? ""}`}
                        tabs={inTab}
                        renderEnv={renderEnvironment}>
                        <ScrollHistory stateKey={screen} target={scrollTarget} />
                        {renderValueProducer(screen.ui, force, ctx, renderEnvironment, false)}
                    </ScrollView>
                </TabContext.Provider>
            </TailwindThemeProvider>
        );
    }
}

export default withNetworkStatus(withTheme(ScreenContentRenderer));
